import { captureException } from '@sentry/browser';
import {
  isLeadTimeArrayValid,
  sortLeadTimeArray,
  calculateAvailabilityAndLeadTime,
} from './helpers/availability-lead-time.js';
import { serializeForm } from './helpers/serialization.js';
import { fetchConfig } from './helpers/fetch';
import { readCartJson } from './shared/utils/cart-response';
import { clampQuantity } from './shared/utils/quantity';
import addToCartController from './shared/utils/product-add-to-cart.js';
import { handleAlgoliaAddedToCartAttribution } from './shared/utils/algolia-attribution';

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();

    this.form = this.querySelector('form');
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this), { signal: this.abortController.signal });

    this.cartNotification = document.querySelector('cart-notification');
    this.submitButton = this.querySelector('button[data-add-to-cart]');

    this.input = this.querySelector('input.quantity__input');
    this.badge = this.querySelector('[data-stock-badge]');

    if (this.badge) {
      this.badgeLabels = this.badge.querySelectorAll('[data-stock-badge-label]');
    }

    this.messageWrapper = this.querySelector('[data-stock-message]');

    if (this.messageWrapper) {
      this.messageWrapperLabels = this.messageWrapper.querySelectorAll('[data-stock-message-label]');
    }

    this._parseOptions();

    this.leadTime = Number(this.submitButton.dataset.leadTime);
    this.availabilityStatus = this.submitButton.dataset.availabilityStatus;
    this.maxQty = 0;

    const cartIconBubbleElement = document.getElementById('cart-icon-bubble');

    cartIconBubbleElement.addEventListener('cart-update', this.onCartUpdated.bind(this), {
      signal: this.abortController.signal,
    });

    if (this.input) {
      this.input.addEventListener('change', this.onQuantityInputChange.bind(this), {
        signal: this.abortController.signal,
      });
    }

    if (this.submitButton) {
      this.submitButton.classList.add('loaded');

      addToCartController.setDisabled(this.submitButton.disabled);

      if (
        this.submitButton.dataset.availabilityStatus !== 'request' &&
        this.submitButton.dataset.availabilityStatus !== 'unavailable'
      ) {
        this.enableAddToCartButton();
      } else {
        this.disableAddToCartButton();
      }
    }
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  _parseOptions() {
    // Badge options
    this.badgeOptions = {
      quantityAlreadyInCart: 0,
      inventoryQuantity: 1,
      inventoryPolicy: 'continue',
      leadTimeArray: [
        { lead_time: 1, max_qty: 2 },
        { lead_time: 10, max_qty: 20 },
      ],
      isValidArrayLeadTime: false,
    };

    // Get options from element data and combine with this.badgeOptions
    if (this.badge?.dataset?.options) {
      const dataOptions = JSON.parse(this.badge.dataset.options);

      this.badgeOptions = {
        ...this.badgeOptions,
        ...dataOptions,
      };

      this.badgeOptions.leadTimeArray = sortLeadTimeArray(this.badgeOptions.leadTimeArray);
      this.badgeOptions.isValidArrayLeadTime = isLeadTimeArrayValid(this.badgeOptions.leadTimeArray);
    }
  }

  /**
   * Handle cart update event to update the current quantity in cart
   * @param {CustomEvent} event
   */
  onCartUpdated(event) {
    const { productQuantity } = event.detail;

    this.badgeOptions.quantityAlreadyInCart = productQuantity;

    this.displayQuantityAlreadyInCart();
  }

  /** Writes the clamped quantity back, so the customer sees what is actually being added. */
  clampQuantityInput() {
    if (!this.input) {
      return;
    }

    const clamped = clampQuantity(this.input.value, this.input.min);

    if (String(clamped) !== this.input.value) {
      this.input.value = clamped;
    }
  }

  onSubmitHandler(evt) {
    evt.preventDefault();

    this.cartNotification.setActiveElement(document.activeElement);

    this.disableAddToCartButton();
    this.submitButton.classList.add('loading');

    this.clampQuantityInput();

    const formData = JSON.parse(serializeForm(this.form));
    const shopifyObjectID = formData?.id != null ? String(formData.id) : '';

    const body = JSON.stringify({
      ...formData,
      sections: this.cartNotification.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
      lead_time: this.leadTime,
      availability: this.availabilityStatus,
    });

    fetch(`${window.routes.cart_add_url}`, { ...fetchConfig('javascript'), body, signal: this.abortController.signal })
      .then(readCartJson)
      .then((parsedState) => {
        // /cart/add.js returns an error payload without sections when the item cannot be
        // added (e.g. stock limit reached); renderContents surfaces the API error.
        if (!parsedState.sections) {
          this.cartNotification.renderContents(parsedState);

          return;
        }

        this.cartNotification.renderContents(parsedState);
        this.resetQuantityInput();

        handleAlgoliaAddedToCartAttribution({ shopifyObjectID });
      })
      .catch((e) => {
        if (e.name === 'AbortError') {
          return;
        }

        captureException(e);
      })
      .finally(() => {
        this.submitButton.classList.remove('loading');
        this.enableAddToCartButton();
      });
  }

  onQuantityInputChange(event) {
    this.clampQuantityInput();

    const currentInputValue = parseInt(event.target.value, 10) + this.badgeOptions.quantityAlreadyInCart;

    const { availabilityStatus, leadTime, previousBucketMaxQuantity } = calculateAvailabilityAndLeadTime(
      this.badgeOptions.leadTimeArray,
      this.badgeOptions.inventoryQuantity,
      this.badgeOptions.inventoryPolicy,
      currentInputValue
    );

    this.leadTime = leadTime;
    this.availabilityStatus = availabilityStatus;
    this.badgeOptions.previousBucketMaxQuantity = previousBucketMaxQuantity;

    this.showActiveStockMessage(availabilityStatus);
    this.showActiveBadgeLabel(availabilityStatus);

    if (['request', 'unavailable'].includes(availabilityStatus)) {
      this.disableAddToCartButton();
    } else {
      this.enableAddToCartButton();

      if (
        (availabilityStatus === 'stock' || this.badgeOptions.previousBucketMaxQuantity === 0) &&
        this.messageWrapper != null
      ) {
        this.hideStockMessages();
      }
    }
  }

  resetQuantityInput() {
    this.querySelector('quantity-input').resetValue();
  }

  showActiveBadgeLabel(badgeType) {
    // Check if badge is available
    if (this.badge) {
      // Loop through all badge labels
      this.badgeLabels.forEach((label) => {
        // First hide all badge labels
        label.classList.remove('badge__label--visible');

        // Show the correct badge label
        if (label?.dataset?.stockBadgeLabel === badgeType) {
          label.classList.add('badge__label--visible');

          if (badgeType === 'backorder') {
            label.textContent = label.dataset.labelTranslation.replace('{{ html }}', this.leadTime);
          }
        }
      });
    }
  }

  hideStockMessages() {
    // Loop through all message labels
    (this.messageWrapperLabels ?? []).forEach((label) => {
      // First hide all message labels
      label.classList.remove('stock-message__label--visible');
    });
  }

  showActiveStockMessage(badgeType) {
    // Loop through all message labels
    (this.messageWrapperLabels ?? []).forEach((label) => {
      // First hide all message labels
      label.classList.remove('stock-message__label--visible');

      // Show the correct badge label
      if (label?.dataset?.stockMessageLabel === badgeType) {
        label.classList.add('stock-message__label--visible');

        if (badgeType === 'backorder' && this.badgeOptions.isValidArrayLeadTime) {
          label.innerHTML = `${this.badgeOptions.backorder
            .replace('{{ lead_time }}', this.leadTime)
            .replace('{{ previous_max_qty }}', this.badgeOptions.previousBucketMaxQuantity)}`;
        }
      }
    });
  }

  disableAddToCartButton() {
    if (this.submitButton !== null) {
      this.submitButton.disabled = true;
      addToCartController.setDisabled(true);
    }
  }

  enableAddToCartButton() {
    if (this.submitButton !== null) {
      this.submitButton.disabled = false;
      this.submitButton.removeAttribute('disabled');
      addToCartController.setDisabled(false);
    }
  }

  displayQuantityAlreadyInCart() {
    if (this.messageWrapper) {
      const quantityInCartContainer = this.messageWrapper.querySelector('[data-already-in-cart]');

      if (this.badgeOptions.quantityAlreadyInCart > 0) {
        const text = quantityInCartContainer.dataset.labelTranslation;

        quantityInCartContainer.innerHTML = text.replace('{{ cart_qty }}', this.badgeOptions.quantityAlreadyInCart);
        quantityInCartContainer.classList.add('stock-message__label--visible');
      } else {
        quantityInCartContainer.classList.remove('stock-message__label--visible');
      }
    }
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}
